import { currentLocale } from '@/lib/locale';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Pencil, Trash2, Filter, Search, SlidersHorizontal, Wrench, Copy, X,
  ArrowUp, ArrowDown, ChevronDown,
} from 'lucide-react';
import { DynamicSelect } from './DynamicSelect';
import { QuipteamsSupportDialog } from './QuipteamsSupportDialog';
import { DeviceStatusBadge, getStatusClasses, isDeviceActive } from './DeviceStatusBadge';
import { usePagination } from '@/hooks/usePagination';
import { isReadyToDeliver, hasAssignedOwner } from '../../../../shared/inventory-status';
import { DELIVERED_KIND_CLASSES } from './delivered-crosscheck-styles';
import { useInventoryOptions } from '@/hooks/useInventoryOptions';
import { useUpdateDevice } from '@/hooks/useDevices';
import type { Device, DeviceFilters } from '../../../../shared/types';
import { useState, useEffect, useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export type InventoryViewTab = 'active' | 'intermediate' | 'inactive' | 'all';

/** Chip de conteo por estado; "Lista para Entregar" viene partida en asignadas / sin asignar. */
export interface StatusChipOption {
  key: string;
  status: string;
  /** Solo para Lista para Entregar: true = ya tiene destinatario (owner @winclap). */
  assigned?: boolean;
  count: number;
}

interface DeviceTableProps {
  devices: Device[];
  isLoading: boolean;
  onEdit: (device: Device) => void;
  onDelete?: (deviceId: string) => void;
  filters: DeviceFilters;
  onFiltersChange: (filters: DeviceFilters) => void;
  // Split Activo/Inactivo (estado en InventoryPage)
  viewTab: InventoryViewTab;
  onViewTabChange: (tab: InventoryViewTab) => void;
  tabCounts: Record<InventoryViewTab, number>;
  statusCounts: StatusChipOption[];
  statusChip: string | null;
  onStatusChipChange: (status: string | null) => void;
}

type ColumnKey =
  | 'fechaCompra'
  | 'proveedor'
  | 'numFactura'
  | 'costo'
  | 'linkFactura'
  | 'nroSerie'
  | 'categoria'
  | 'modelo'
  | 'nroModelo'
  | 'ramGB'
  | 'discoGB'
  | 'clapBase'
  | 'status'
  | 'abm'
  | 'owner'
  | 'comments';

const DEFAULT_HIDDEN: ColumnKey[] = ['fechaCompra', 'proveedor', 'numFactura', 'costo', 'linkFactura', 'nroModelo'];
const COLUMNS_STORAGE_KEY = 'inventory.visibleColumns';

type SortKey = 'fechaCompra' | 'status' | 'owner' | 'modelo';
interface SortState { key: SortKey; dir: 'asc' | 'desc' }

/** Badge de status clickeable: dropdown para cambiar el estado desde la fila. */
function QuickStatusBadge({ device }: { device: Device }) {
  const { t } = useTranslation();
  const { data: statusOptions = [] } = useInventoryOptions('status');
  const updateDevice = useUpdateDevice();
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);

  // Dedupe por valor trimmed ('Localizar ' y 'Localizar' = una opción)
  const options = useMemo(() => {
    const seen = new Map<string, string>();
    for (const o of statusOptions) {
      const key = o.value.trim().toLowerCase();
      if (!seen.has(key)) seen.set(key, o.value.trim());
    }
    return [...seen.values()];
  }, [statusOptions]);

  const applyStatus = (status: string) => {
    updateDevice.mutate(
      { deviceId: device.deviceId, updates: { status } },
      { onSuccess: () => toast.success(t('devices.quick_status.updated_toast', { status })) }
    );
  };

  const handleSelect = (status: string) => {
    if (status.trim().toLowerCase() === (device.status || '').trim().toLowerCase()) return;
    // Cruce activo → inactivo: confirmar (el equipo "desaparece" de la tab activa)
    if (isDeviceActive(device.status) && !isDeviceActive(status)) {
      setPendingStatus(status);
    } else {
      applyStatus(status);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            title={t('devices.quick_status.change_status')}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-opacity hover:opacity-80 ${getStatusClasses(device.status)}`}
          >
            {(device.status || '').trim() || '—'}
            <ChevronDown className="h-3 w-3 opacity-60" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
          {options.map((status) => (
            <DropdownMenuItem key={status} onClick={() => handleSelect(status)}>
              <DeviceStatusBadge status={status} />
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={!!pendingStatus} onOpenChange={(open) => !open && setPendingStatus(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('devices.quick_status.confirm_inactive_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('devices.quick_status.confirm_inactive_desc', { status: pendingStatus ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('devices.delete_dialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingStatus) applyStatus(pendingStatus);
                setPendingStatus(null);
              }}
            >
              {t('devices.quick_status.change_status')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function DeviceTable({
  devices,
  isLoading,
  onEdit,
  onDelete,
  filters,
  onFiltersChange,
  viewTab,
  onViewTabChange,
  tabCounts,
  statusCounts,
  statusChip,
  onStatusChipChange,
}: DeviceTableProps) {
  const { t } = useTranslation();

  const COLUMNS = useMemo(() => [
    { key: 'fechaCompra' as ColumnKey, label: t('devices.table_headers.purchase_date') },
    { key: 'proveedor' as ColumnKey, label: t('devices.table_headers.supplier') },
    { key: 'numFactura' as ColumnKey, label: t('devices.table_headers.invoice_num') },
    { key: 'costo' as ColumnKey, label: t('devices.table_headers.cost') },
    { key: 'linkFactura' as ColumnKey, label: t('devices.table_headers.invoice_link') },
    { key: 'nroSerie' as ColumnKey, label: t('devices.table_headers.serial') },
    { key: 'categoria' as ColumnKey, label: t('devices.table_headers.category') },
    { key: 'modelo' as ColumnKey, label: t('devices.table_headers.model') },
    { key: 'nroModelo' as ColumnKey, label: t('devices.table_headers.model_number') },
    { key: 'ramGB' as ColumnKey, label: t('devices.table_headers.ram') },
    { key: 'discoGB' as ColumnKey, label: t('devices.table_headers.disk') },
    { key: 'clapBase' as ColumnKey, label: t('devices.table_headers.clapbase') },
    { key: 'status' as ColumnKey, label: t('devices.table_headers.status') },
    { key: 'abm' as ColumnKey, label: t('devices.table_headers.abm') },
    { key: 'owner' as ColumnKey, label: t('devices.table_headers.owner') },
    { key: 'comments' as ColumnKey, label: t('devices.table_headers.comments') },
  ], [t]);

  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [quipteamsDevice, setQuipteamsDevice] = useState<Device | null>(null);
  const [sort, setSort] = useState<SortState | null>(null);
  const [visibleCols, setVisibleCols] = useState<Set<ColumnKey>>(() => {
    // Preferencia persistida (antes se perdía al cambiar de tab o recargar)
    try {
      const saved = localStorage.getItem(COLUMNS_STORAGE_KEY);
      if (saved) return new Set(JSON.parse(saved) as ColumnKey[]);
    } catch { /* default */ }
    return new Set(COLUMNS.map(c => c.key).filter(k => !DEFAULT_HIDDEN.includes(k as ColumnKey)));
  });

  const toggleColumn = (key: ColumnKey) => {
    setVisibleCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      try {
        localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify([...next]));
      } catch { /* quota — ignorar */ }
      return next;
    });
  };

  const updateFilter = (key: keyof DeviceFilters, value: string[] | string | undefined) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    });
  };

  const hasActiveFilters = !!(filters.categoria?.length || filters.clapBase?.length || filters.search);
  const clearFilters = () => {
    setSearchInput('');
    onFiltersChange({});
    onStatusChipChange(null);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      updateFilter('search', searchInput || undefined);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (filters.search !== searchInput) {
      setSearchInput(filters.search || '');
    }
  }, [filters.search]);

  const getABMColor = (abm?: string) => {
    const abmColors: Record<string, string> = {
      'ABM': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      'NO ENROLADA': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      'No Aplica': 'bg-gray-100 text-gray-800 dark:bg-muted dark:text-muted-foreground',
      'Vendida': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    };

    const fallback = 'bg-gray-100 text-gray-800 dark:bg-muted dark:text-muted-foreground';
    return abm ? abmColors[abm] || fallback : fallback;
  };

  const col = (key: ColumnKey) => visibleCols.has(key);

  // ── Sort client-side ─────────────────────────────────────────────────────────
  const toggleSort = (key: SortKey) =>
    setSort(prev => (prev?.key === key ? (prev.dir === 'asc' ? { key, dir: 'desc' } : null) : { key, dir: 'asc' }));

  const sortedDevices = useMemo(() => {
    if (!sort) return devices;
    const val = (d: Device): string => {
      switch (sort.key) {
        case 'fechaCompra': return d.fechaCompra || '';
        case 'status': return (d.status || '').trim().toLowerCase();
        case 'owner': return (d.owner || '').toLowerCase();
        case 'modelo': return (d.modelo || '').toLowerCase();
      }
    };
    const mul = sort.dir === 'asc' ? 1 : -1;
    return [...devices].sort((a, b) => val(a).localeCompare(val(b)) * mul);
  }, [devices, sort]);
  // Paginado (feedback 30/09: el inventario era un "scroll eterno"): monta 25 filas por vez
  const { pageItems: pageDevices, footer: paginationFooter } = usePagination(sortedDevices, 25);

  const SortableHead = ({ sortKey, label, className }: { sortKey: SortKey; label: string; className?: string }) => (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => toggleSort(sortKey)}
        className="inline-flex items-center gap-1 hover:text-foreground"
      >
        {label}
        {sort?.key === sortKey &&
          (sort.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </TableHead>
  );

  const copySerial = async (serial: string) => {
    try {
      await navigator.clipboard.writeText(serial);
      toast.success(t('devices.serial_copied'));
    } catch {
      toast.error(serial); // fallback: al menos mostrarlo completo
    }
  };

  // Cruce de Entregados con el community map: etiqueta por fila (una sola query, cacheada)
  const { data: crossCheck } = trpc.inventory.getDeliveredCrossCheck.useQuery(undefined, { staleTime: 60_000 });
  const duplicateIds = useMemo(() => new Set(crossCheck?.duplicateDeviceIds ?? []), [crossCheck]);

  const viewTabs: { key: InventoryViewTab; label: string }[] = [
    { key: 'active', label: t('devices.split.active') },
    { key: 'intermediate', label: t('devices.split.intermediate') },
    { key: 'inactive', label: t('devices.split.inactive') },
    { key: 'all', label: t('devices.split.all') },
  ];

  return (
    <div className="space-y-4">
      {/* Split Activo/Inactivo + búsqueda */}
      <div className="flex flex-wrap items-center gap-2 px-4 pt-4">
        {viewTabs.map(({ key, label }) => (
          <Button
            key={key}
            size="sm"
            variant={viewTab === key ? 'default' : 'outline'}
            className="h-8"
            onClick={() => onViewTabChange(key)}
          >
            {label}
            <span className={cn(
              'ml-1.5 rounded-full px-1.5 text-xs tabular-nums',
              viewTab === key ? 'bg-background/20' : 'bg-muted'
            )}>
              {tabCounts[key]}
            </span>
          </Button>
        ))}
        <div className="relative ml-auto w-64 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('devices.search_placeholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10 h-9"
          />
        </div>
      </div>

      {/* Chips de conteo por estado (filtran) + toggles de panel */}
      <div className="flex flex-wrap items-center gap-1.5 px-4">
        {statusCounts.map(({ key, status, assigned, count }) => (
          <button
            key={key}
            type="button"
            onClick={() => onStatusChipChange(statusChip === key ? null : key)}
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-all',
              getStatusClasses(status),
              assigned === false && 'border-dashed',
              statusChip === key ? 'ring-2 ring-primary ring-offset-1' : 'opacity-80 hover:opacity-100'
            )}
            title={
              assigned === true ? t('devices.split.ready_assigned_hint')
              : assigned === false ? t('devices.split.ready_unassigned_hint')
              : undefined
            }
          >
            {status}
            {assigned !== undefined && (
              <span className="font-normal opacity-80">
                · {assigned ? t('devices.split.ready_assigned') : t('devices.split.ready_unassigned')}
              </span>
            )}
            <span className="tabular-nums font-semibold">{count}</span>
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          {(hasActiveFilters || statusChip) && (
            <Button variant="ghost" size="sm" className="h-8 gap-1 text-muted-foreground" onClick={clearFilters}>
              <X className="h-3.5 w-3.5" />
              {t('devices.filters_panel.clear_filters')}
            </Button>
          )}
          <Button
            variant={showFilters ? 'default' : 'outline'}
            size="sm"
            className="h-8"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="mr-1.5 h-3.5 w-3.5" />
            {t('devices.filters')}
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8">
                <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" />
                {t('devices.columns')}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56">
              <p className="text-sm font-medium mb-3">{t('devices.visible_columns')}</p>
              <div className="space-y-2">
                {COLUMNS.map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-2">
                    <Checkbox
                      id={`col-${key}`}
                      checked={visibleCols.has(key)}
                      onCheckedChange={() => toggleColumn(key)}
                    />
                    <label htmlFor={`col-${key}`} className="text-sm cursor-pointer">
                      {label}
                    </label>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Filter Panel (el filtro de status vive en los chips de arriba) */}
      {showFilters && (
        <div className="grid gap-4 p-4 mx-4 bg-muted/30 rounded-lg md:grid-cols-2">
          <div>
            <label className="text-sm font-medium mb-2 block">{t('devices.filters_panel.category')}</label>
            <DynamicSelect
              optionType="categoria"
              value={filters.categoria?.[0] || ''}
              onValueChange={(val) => updateFilter('categoria', val ? [val] : undefined)}
              placeholder={t('devices.all_f')}
              allowCreate={false}
              allowClear
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">{t('devices.filters_panel.clapbase')}</label>
            <DynamicSelect
              optionType="clapbase"
              value={filters.clapBase?.[0] || ''}
              onValueChange={(val) => updateFilter('clapBase', val ? [val] : undefined)}
              placeholder={t('devices.all_f')}
              allowCreate={false}
              allowClear
            />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border overflow-x-auto">
        <Table>
        <TableHeader>
          <TableRow>
            {col('fechaCompra') && <SortableHead sortKey="fechaCompra" label={t('devices.table_headers.purchase_date')} />}
            {col('proveedor') && <TableHead>{t('devices.table_headers.supplier')}</TableHead>}
            {col('numFactura') && <TableHead>{t('devices.table_headers.invoice_num')}</TableHead>}
            {col('costo') && <TableHead>{t('devices.table_headers.cost')}</TableHead>}
            {col('linkFactura') && <TableHead>{t('devices.table_headers.invoice_link')}</TableHead>}
            {col('nroSerie') && <TableHead className="w-[120px]">{t('devices.table_headers.serial')}</TableHead>}
            {col('categoria') && <TableHead>{t('devices.table_headers.category')}</TableHead>}
            {col('modelo') && <SortableHead sortKey="modelo" label={t('devices.table_headers.model')} />}
            {col('nroModelo') && <TableHead>{t('devices.table_headers.model_number')}</TableHead>}
            {col('ramGB') && <TableHead>{t('devices.table_headers.ram')}</TableHead>}
            {col('discoGB') && <TableHead>{t('devices.table_headers.disk')}</TableHead>}
            {col('clapBase') && <TableHead>{t('devices.table_headers.clapbase')}</TableHead>}
            {col('status') && <SortableHead sortKey="status" label={t('devices.table_headers.status')} />}
            {col('abm') && <TableHead>{t('devices.table_headers.abm')}</TableHead>}
            {col('owner') && <SortableHead sortKey="owner" label={t('devices.table_headers.owner')} />}
            {col('comments') && <TableHead>{t('devices.table_headers.comments')}</TableHead>}
            <TableHead className="text-right">{t('devices.table_headers.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={visibleCols.size + 1} className="h-24 text-center text-muted-foreground">
                {t('devices.loading')}
              </TableCell>
            </TableRow>
          ) : sortedDevices.length === 0 ? (
            <TableRow>
              <TableCell colSpan={visibleCols.size + 1} className="h-24 text-center text-muted-foreground">
                {filters.search || filters.categoria || filters.clapBase || statusChip
                  ? t('devices.no_results')
                  : t('devices.no_devices')}
              </TableCell>
            </TableRow>
          ) : (
            pageDevices.map((device) => (
            <TableRow key={device.deviceId}>
              {col('fechaCompra') && (
                <TableCell className="text-sm">
                  {device.fechaCompra ? new Date(device.fechaCompra).toLocaleDateString(currentLocale()) : '-'}
                </TableCell>
              )}
              {col('proveedor') && (
                <TableCell className="max-w-[120px] truncate">
                  {device.proveedor || '-'}
                </TableCell>
              )}
              {col('numFactura') && (
                <TableCell className="max-w-[100px] truncate">
                  {device.numFactura || '-'}
                </TableCell>
              )}
              {col('costo') && (
                <TableCell className="text-sm">
                  {device.costo ? `$${device.costo.toLocaleString(currentLocale())}` : '-'}
                </TableCell>
              )}
              {col('linkFactura') && (
                <TableCell className="max-w-[150px] truncate">
                  {device.linkFactura ? (
                    <a href={device.linkFactura} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {t('devices.view_invoice')}
                    </a>
                  ) : '-'}
                </TableCell>
              )}
              {col('nroSerie') && (
                <TableCell className="font-mono text-sm">
                  <span className="inline-flex items-center gap-1" title={device.nroSerie}>
                    {device.nroSerie.slice(0, 10)}
                    {device.nroSerie.length > 10 && '…'}
                    <button
                      type="button"
                      onClick={() => copySerial(device.nroSerie)}
                      title={t('devices.copy_serial')}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  </span>
                </TableCell>
              )}
              {col('categoria') && <TableCell>{device.categoria}</TableCell>}
              {col('modelo') && <TableCell className="font-medium">{device.modelo}</TableCell>}
              {col('nroModelo') && <TableCell>{device.nroModelo || '-'}</TableCell>}
              {col('ramGB') && <TableCell>{device.ramGB ? `${device.ramGB} GB` : '-'}</TableCell>}
              {col('discoGB') && <TableCell>{device.discoGB ? `${device.discoGB} GB` : '-'}</TableCell>}
              {col('clapBase') && <TableCell>{device.clapBase}</TableCell>}
              {col('status') && (
                <TableCell>
                  <div className="flex flex-col gap-0.5">
                    <QuickStatusBadge device={device} />
                    {/* Entregado: a quién (cruce con el community map) + duplicado (Sol 15/09) */}
                    {crossCheck && crossCheck.byDevice[device.deviceId] && crossCheck.byDevice[device.deviceId] !== 'clapper' && (
                      <span className={cn('text-[11px] leading-none', DELIVERED_KIND_CLASSES[crossCheck.byDevice[device.deviceId]])}>
                        {t(`devices.crosscheck.kind.${crossCheck.byDevice[device.deviceId]}`)}
                      </span>
                    )}
                    {crossCheck && duplicateIds.has(device.deviceId) && (
                      <span className="text-[11px] leading-none text-purple-700 dark:text-purple-300">
                        {t('devices.crosscheck.duplicate_tag')}
                      </span>
                    )}
                    {/* Lista para Entregar: ¿ya tiene destinatario? (Sol 15/09) */}
                    {isReadyToDeliver(device.status) && (
                      <span className={cn('text-[11px] leading-none', hasAssignedOwner(device.owner) ? 'text-blue-700 dark:text-blue-300' : 'text-muted-foreground')}>
                        {hasAssignedOwner(device.owner)
                          ? t('devices.split.ready_assigned_to', { owner: (device.owner || '').split('@')[0] })
                          : t('devices.split.ready_unassigned')}
                      </span>
                    )}
                  </div>
                </TableCell>
              )}
              {col('abm') && (
                <TableCell>
                  {device.abm && (
                    <Badge className={getABMColor(device.abm)} variant="outline">
                      {device.abm}
                    </Badge>
                  )}
                </TableCell>
              )}
              {col('owner') && (
                <TableCell className="max-w-[150px] truncate">
                  {device.owner || (
                    <span className="text-muted-foreground italic">{t('devices.unassigned')}</span>
                  )}
                </TableCell>
              )}
              {col('comments') && (
                <TableCell className="max-w-[200px] truncate">
                  {device.comments || '-'}
                </TableCell>
              )}
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setQuipteamsDevice(device)}
                    title={t('devices.quipteams_support_title')}
                  >
                    <Wrench className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(device)}
                    title={t('devices.edit_title')}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {onDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(device.deviceId)}
                      title={t('devices.delete_title')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))
          )}
        </TableBody>
      </Table>
      </div>

      {paginationFooter && <div className="px-4">{paginationFooter}</div>}

      {/* Results count (con más de una página, el pie del paginado ya dice "x–y de z") */}
      {sortedDevices.length > 0 && !paginationFooter && (
        <div className="text-sm text-muted-foreground px-4 pb-4">
          {sortedDevices.length !== 1
            ? t('devices.showing_plural', { count: sortedDevices.length })
            : t('devices.showing_one', { count: sortedDevices.length })}
        </div>
      )}

      <QuipteamsSupportDialog
        device={quipteamsDevice}
        open={!!quipteamsDevice}
        onClose={() => setQuipteamsDevice(null)}
      />
    </div>
  );
}
